import { useState } from 'react'
import Modal from '@cloudscape-design/components/modal'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import FormField from '@cloudscape-design/components/form-field'
import Input from '@cloudscape-design/components/input'
import Textarea from '@cloudscape-design/components/textarea'
import Select from '@cloudscape-design/components/select'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Alert from '@cloudscape-design/components/alert'
import { buildCloudHeaders } from '@/engine/api-client'

const TYPE_OPTIONS = [
  { value: 'bug', label: '🐛 Bug Report' },
  { value: 'feature', label: '💡 Feature Request' },
  { value: 'feedback', label: '💬 General Feedback' },
]

interface Props {
  onClose: () => void
}

export function FeedbackModal({ onClose }: Props) {
  const [type, setType] = useState(TYPE_OPTIONS[2])
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!subject.trim() || !message.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      // Always send to the prod API — feedback goes to the internal account, not the customer's
      const prodApiUrl = import.meta.env.VITE_API_URL || '/api'
      let headers: Record<string, string> = { 'Content-Type': 'application/json' }
      try {
        const cloudHeaders = await buildCloudHeaders()
        headers = { ...headers, ...cloudHeaders }
      } catch { /* no auth in local mode — that's fine, feedback route is unauthenticated */ }

      const res = await fetch(`${prodApiUrl}/feedback`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          type: type.value,
          subject: subject.trim(),
          message: message.trim(),
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || `Server error ${res.status}`)
      }
      setSubmitted(true)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      visible
      onDismiss={onClose}
      header="Submit Feedback"
      footer={
        submitted ? (
          <Box float="right">
            <Button variant="primary" onClick={onClose}>Close</Button>
          </Box>
        ) : (
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onClose}>Cancel</Button>
              <Button variant="primary" onClick={handleSubmit} loading={submitting} disabled={!subject.trim() || !message.trim()}>
                Submit
              </Button>
            </SpaceBetween>
          </Box>
        )
      }
    >
      {submitted ? (
        <Alert type="success">Thank you for your feedback. The team has been notified.</Alert>
      ) : (
        <SpaceBetween size="m">
          <FormField label="Type">
            <Select
              selectedOption={type}
              onChange={({ detail }) => setType(detail.selectedOption as typeof type)}
              options={TYPE_OPTIONS}
            />
          </FormField>
          <FormField label="Subject">
            <Input value={subject} onChange={({ detail }) => setSubject(detail.value)} placeholder="Brief summary" />
          </FormField>
          <FormField label="Details">
            <Textarea value={message} onChange={({ detail }) => setMessage(detail.value)} placeholder="Describe the issue or suggestion..." rows={5} />
          </FormField>
          {error && <Alert type="error">{error}</Alert>}
        </SpaceBetween>
      )}
    </Modal>
  )
}
